import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  inject,
  input,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Nullable } from 'primeng/ts-helpers';
import { IconKey } from '../../../../../whitelabel/icon-key.enum';
import { IconProviderService } from '../../../../whitelabel/services/icon-provider.service';
import { ButtonFocusHandlerDirective } from './button-focus-handler.directive';
import { ButtonCategory } from './contract/button-category.contract';
import { ButtonIconPosition } from './contract/button-icon-position.contract';
import { ButtonSeverity } from './contract/button-severity.contract';
import { ButtonTooltip } from './contract/button-tooltip.contract';

// PRIMARY (primary)
// SECONDARY (secondary + outlined)
// GHOST (secondary + text button)
// DANGER (danger)

const BUTTON_SEVERITY_MAP: Record<ButtonCategory, ButtonSeverity> = {
  primary: 'primary',
  secondary: 'secondary',
  ghost: 'secondary',
  danger: 'danger',
};

@Component({
  selector: 'osp-button',
  standalone: true,
  imports: [ButtonModule, TooltipModule, CommonModule, ButtonFocusHandlerDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ButtonComponent {
  // TODO: Find a way to exclude this service (IconProviderService)
  private iconProviderService = inject(IconProviderService);
  @HostBinding('class.osp-button') protected hostCssClassEnabled = true;

  $label = input<string | undefined>(undefined, { alias: 'label' });
  $category = input<ButtonCategory, ButtonCategory>('primary', {
    alias: 'category',
    transform: (value: ButtonCategory | undefined): ButtonCategory => value ?? 'primary',
  });
  $rounded = input<boolean>(false, { alias: 'rounded' });
  $icon = input<string | Nullable>(undefined, { alias: 'icon' });
  $iconPos = input<ButtonIconPosition>('left', { alias: 'iconPos' });
  $disabled = input<boolean>(false, { alias: 'disabled' });
  $loading = input<boolean>(false, { alias: 'loading' });
  $loadingIcon = input<string>(this.iconProviderService.getIcon(IconKey.GlobalButtonSpinner), { alias: 'loadingIcon' });
  $iconOnly = input<boolean>(false, { alias: 'iconOnly' }); // Set "true" if the button has a custom projected content and it should look like an icon button
  $tooltip = input<ButtonTooltip | undefined>(undefined, { alias: 'tooltip' });
  $tooltipPosition = input<'right' | 'left' | 'top' | 'bottom'>('top', { alias: 'tooltipPosition' });
  $badge = input<string | undefined>(undefined, { alias: 'badge' });
  $badgeClass = input<string | undefined>(undefined, { alias: 'badgeClass' });
  $cssClass = input<string>('', { alias: 'cssClass' });
  $ariaLabel = input<string>('');
  $tabIndex = input<number | undefined>(undefined, { alias: 'tabIndex' });

  $onClick = output<MouseEvent | PointerEvent>({ alias: 'onClick' });

  protected $severity = computed(() => BUTTON_SEVERITY_MAP[this.$category()]);
  protected $outlined = computed(() => this.$category() === 'secondary');
  protected $text = computed(() => this.$category() === 'ghost');

  public handleClick(event: MouseEvent | PointerEvent): void {
    if (this.$disabled() || this.$loading()) {
      event.stopPropagation();
      return;
    }
    this.$onClick.emit(event);
  }
}
